/**
 * Edge renderer for a connection that jumps a layout row.
 *
 * React Flow renders nodes ABOVE edges, so a straight line between two non-adjacent
 * rows disappears behind whatever card sits in between. The live q_1_1 has exactly
 * one: `Q1.1.4 --否--> Q1.1.5` passes through `Q1.1.4.1`, and before this the "not a
 * migration" branch was simply invisible — the kind of missing line that makes a
 * reader believe the graph says something it does not.
 *
 * None of React Flow's built-in edge types avoid obstacles, so the detour is drawn
 * here: drop out of the source, run sideways into a reserved lane in the left gutter,
 * down past the rows being skipped, then back in to the target. The lane's x comes
 * from the projection (`laneX`), which is also what reserved the gutter, so this
 * component needs no knowledge of the layout.
 */
import { BaseEdge, EdgeLabelRenderer, type EdgeProps } from '@xyflow/react';

import { roundedPath } from '../../utils/edgePaths';
import type { CpaEdge } from '../../utils/projection';
import EdgeLabel from './EdgeLabel';

/** Vertical stub before turning sideways, so the arrow leaves the card cleanly. */
const STUB = 14;
export default function SkipEdge({
  id,
  sourceX,
  sourceY,
  targetX,
  targetY,
  style,
  markerEnd,
  data,
}: EdgeProps) {
  const d = data as { laneX?: number; edge?: CpaEdge } | undefined;
  const laneX = d?.laneX;
  // Without a lane there is nothing to detour into; a straight line is still better
  // than nothing, and the projection only sets this type when a lane exists.
  const lane = laneX ?? Math.min(sourceX, targetX) - 40;

  const pts = [
    { x: sourceX, y: sourceY },
    { x: sourceX, y: sourceY + STUB },
    { x: lane, y: sourceY + STUB },
    { x: lane, y: targetY - STUB },
    { x: targetX, y: targetY - STUB },
    { x: targetX, y: targetY },
  ];

  return (
    <>
      <BaseEdge id={id} path={roundedPath(pts)} style={style} markerEnd={markerEnd} />
      {d?.edge && (
        <EdgeLabelRenderer>
          {/* Pinned to the middle of the vertical run rather than the path midpoint:
              a detour's midpoint lands in the gutter, where the label would sit on
              top of the lane it belongs to. */}
          <div
            className="nodrag nopan cpa-edge-label-wrap"
            style={{
              transform: `translate(-50%, -50%) translate(${lane}px, ${
                (sourceY + targetY) / 2
              }px)`,
            }}
          >
            <EdgeLabel edge={d.edge} />
          </div>
        </EdgeLabelRenderer>
      )}
    </>
  );
}
